import { FC } from 'react'
import { PrescricaoPacienteView } from '../../modules/apps/atendimento_medicos/prescricao/core/_model'
import { PrescricaoSituation } from '../../../_metronic/helpers'
import { rotuloNumeroSncr } from '../../modules/apps/sncr/core/_models'

type IResumoPrescricaoCard = {
  prescricao: PrescricaoPacienteView
}

type StatusChip = {
  texto: string
  tom: 'sucesso' | 'neutro' | 'alerta'
}

/** Retorna apenas strings não-vazias; caso contrário, undefined (omissão elegante). */
const textoOuOmite = (valor?: string | null): string | undefined => {
  if (typeof valor !== 'string') return undefined
  const limpo = valor.trim()
  return limpo.length > 0 ? limpo : undefined
}

/** Deriva o status de assinatura a partir dos campos já presentes no payload. */
const derivarStatus = (p: PrescricaoPacienteView): StatusChip | undefined => {
  if (p.utilizada) return { texto: 'Receita já dispensada', tom: 'neutro' }
  if (p.valida === false) return { texto: 'Receita expirada', tom: 'alerta' }
  if (p.situacao === PrescricaoSituation.Cancelada) return { texto: 'Cancelada', tom: 'alerta' }
  if (p.assinada || p.situacao === PrescricaoSituation.Assinada) {
    return { texto: 'Assinada digitalmente', tom: 'sucesso' }
  }
  if (p.valida) return { texto: 'Receita válida', tom: 'sucesso' }
  if (p.situacao === PrescricaoSituation.Finalizada) {
    return { texto: 'Finalizada', tom: 'neutro' }
  }
  return undefined
}

/**
 * Card "Resumo da Prescrição" do Hub público.
 * Apresentacional: lê apenas campos já presentes em PrescricaoPacienteView,
 * sem chamadas de API. Cada linha só aparece se o valor existir (sem rótulo
 * vazio, "undefined" ou "null"). Tokens de marca Lauduz.
 */
export const ResumoPrescricaoCard: FC<IResumoPrescricaoCard> = ({ prescricao }) => {
  const medico = textoOuOmite(prescricao.medico?.nome)
  const crm = textoOuOmite(prescricao.medico?.crmUf)
  const data =
    textoOuOmite(prescricao.dataFormatada) ?? textoOuOmite(prescricao.dataValidadeFormatada)
  const numeroSncr = rotuloNumeroSncr(prescricao.numeroSncr, prescricao.tipoReceitaSncr)
  const status = derivarStatus(prescricao)

  const temLinhas = medico || crm || data || numeroSncr

  return (
    <div className='resumo-prescricao'>
      <div className='resumo-prescricao__header'>
        <span className='resumo-prescricao__icon' aria-hidden='true'>
          <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
            <path
              d='M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z'
              stroke='currentColor'
              strokeWidth='1.6'
              strokeLinejoin='round'
            />
            <path
              d='M14 3v5h5M9 13h6M9 17h6'
              stroke='currentColor'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </span>
        <h2 className='resumo-prescricao__title'>Resumo da prescrição</h2>
      </div>

      {temLinhas && (
        <dl className='resumo-prescricao__list'>
          {medico && (
            <div className='resumo-prescricao__row'>
              <dt className='resumo-prescricao__label'>Médico</dt>
              <dd className='resumo-prescricao__value'>{medico}</dd>
            </div>
          )}
          {crm && (
            <div className='resumo-prescricao__row'>
              <dt className='resumo-prescricao__label'>CRM</dt>
              <dd className='resumo-prescricao__value'>{crm}</dd>
            </div>
          )}
          {data && (
            <div className='resumo-prescricao__row'>
              <dt className='resumo-prescricao__label'>Emissão</dt>
              <dd className='resumo-prescricao__value'>{data}</dd>
            </div>
          )}
          {numeroSncr && (
            <div className='resumo-prescricao__row'>
              <dt className='resumo-prescricao__label'>Nº SNCR</dt>
              <dd className='resumo-prescricao__value'>{numeroSncr}</dd>
            </div>
          )}
        </dl>
      )}

      {status && (
        <span className={`resumo-prescricao__status resumo-prescricao__status--${status.tom}`}>
          {status.texto}
        </span>
      )}
    </div>
  )
}
